import React from "react";

const AccessControlsAuditTrails: React.FC = () => {
  const accessControlSections = [
    {
      id: "customer-access",
      title: "1. Customer Access Controls (Role-Based Access Control)",
      description:
        "Our platform is built on a Role-Based Access Control (RBAC) model to ensure your users only have access to the data they need for their job. You (the Customer Admin) control these permissions.",
      items: [
        {
          id: "administrator",
          title: "Administrator",
          description:
            "Full access to the account. Can configure integrations, manage billing, add/remove users, and define permissions for other roles.",
        },
        {
          id: "recruiter",
          title: "Recruiter / TA Manager",
          description:
            "Full access to assigned job requisitions. Can create new roles, review candidates, see AI scores, and move candidates through the pipeline.",
        },
        {
          id: "hiring-manager",
          title: "Hiring Manager",
          description:
            "Limited access. Can only view and provide feedback on candidates for the specific job(s) they have been assigned to. Cannot see other requisitions or admin-level settings.",
        },
      ],
    },
    {
      id: "secure-auth",
      title: "2. Secure Authentication",
      items: [
        {
          id: "strong-passwords",
          title: "Strong Passwords",
          description:
            "We enforce strong password complexity requirements for all user accounts.",
        },
        {
          id: "sso",
          title: "Single Sign-On (SSO)",
          description:
            "We support SSO integration (available on Enterprise Plans) with your corporate identity provider (e.g., Okta, Azure AD, Google Workspace). This allows you to enforce your own company's authentication policies (like 2FA/MFA) for all users.",
        },
      ],
    },
    {
      id: "internal-access",
      title: "3. Internal Access Controls (25th & Staffing Personnel)",
      description:
        "Access to customer data by 25TH & STAFFING LIMITED employees is strictly controlled, logged, and audited.",
      items: [
        {
          id: "least-privilege",
          title: "Principle of Least Privilege",
          description:
            "Employees are only granted the minimum access necessary to perform their job functions (e.g., a support engineer).",
        },
        {
          id: "internal-mfa",
          title: "Secure Authentication",
          description:
            "All internal access requires Multi-Factor Authentication (MFA).",
        },
        {
          id: "explicit-permission",
          title: "Explicit Permission",
          description:
            "Access to customer production environments is restricted to a small, authorized team. Support engineers cannot access customer data without a valid, auditable reason (e.g., a customer-filed support ticket).",
        },
      ],
    },
  ];

  const auditTrailSections = [
    {
      id: "audit-trails",
      title: "4. Audit Trails",
      description:
        "We maintain comprehensive logs to ensure all actions within the platform are tracked and auditable.",
      items: [
        {
          id: "customer-audits",
          title: "Customer-Facing Audits",
          description:
            "Administrators on an Enterprise Plan can view a log of key activities within their account, such as:",
          activities: [
            "User logins and failed login attempts.",
            "Creation or deletion of a job requisition.",
            "Changes to user permissions.",
            "Candidate profile deletion (for compliance).",
          ],
        },
        {
          id: "internal-audits",
          title: "Internal Audits",
          description:
            "We maintain detailed, immutable logs of all internal actions, including any access to customer data by 25TH & STAFFING personnel. These logs are monitored for suspicious activity and reviewed regularly.",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-5">
          <h1 className="text-[20px] md:text-[35px] font-[500] mb-2">
            Access Controls & Audit Trails
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Controlling and monitoring data access is fundamental to our
            security model. We provide robust tools for our customers to manage
            their own users and maintain strict internal policies for our own
            personnel.
          </p>
        </div>

        {/* Access Controls Section */}
        <div className="space-y-4 mb-5">
          {accessControlSections.map((section) => (
            <section
              key={section.id}
              className="bg-white rounded-[10px] shadow-sm border border-[gray]/20 p-6 sm:p-8 hover:shadow-md transition-shadow duration-300"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {section.title}
              </h2>

              {section.description && (
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {section.description}
                </p>
              )}

              <div className="space-y-4">
                {section.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="pl-6 border-l-4 border-blue-500 bg-blue-50/50 rounded-r-lg p-4 hover:bg-blue-50 transition-colors duration-200"
                  >
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Audit Trails Section */}
        <div className="space-y-4">
          {auditTrailSections.map((section) => (
            <section
              key={section.id}
              className="bg-white rounded-[10px] shadow-sm border border-[gray]/20 p-6 sm:p-8 hover:shadow-md transition-shadow duration-300"
            >
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                {section.title}
              </h2>

              {section.description && (
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {section.description}
                </p>
              )}

              <div className="space-y-4">
                {section.items.map((item) => (
                  <div
                    key={item.id}
                    className="pl-6 border-l-4 border-blue-500 bg-blue-50/50 rounded-r-lg p-4 hover:bg-blue-50 transition-colors duration-200"
                  >
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 mb-3 leading-relaxed">
                      {item.description}
                    </p>

                    {item.activities && (
                      <ul className="space-y-2 mt-3">
                        {item.activities.map((activity, activityIndex) => (
                          <li
                            key={activityIndex}
                            className="flex items-start text-gray-600"
                          >
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <span className="leading-relaxed">{activity}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800">
            <span className="text-sm text-gray-600">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessControlsAuditTrails;
